/** Machine à états du mode solo (miroir déformant) — PURE. */
import type { CalibrationError } from '../detection/calibration';
import type { SoloMode } from './records';

export type SoloPhase = 'intro' | 'calibrating' | 'countdown' | 'playing' | 'paused' | 'over';

export interface SoloState {
  readonly phase: SoloPhase;
  readonly mode: SoloMode;
  readonly calibrationError: CalibrationError | null;
  readonly startedAt: number;
  readonly pausedAt: number;
  /** Temps cumulé en pause (visage perdu), exclu du temps tenu. */
  readonly pausedTotal: number;
  readonly heldMs: number | null;
}

export type SoloEvent =
  | { readonly type: 'start'; readonly mode: SoloMode }
  | { readonly type: 'calibrated' }
  | { readonly type: 'calibrationFailed'; readonly error: CalibrationError }
  | { readonly type: 'countdownDone'; readonly t: number }
  /** Rire (ou clignement) détecté ; `t` = début du rire. */
  | { readonly type: 'lost'; readonly t: number }
  | { readonly type: 'faceLost'; readonly t: number }
  | { readonly type: 'faceBack'; readonly t: number }
  | { readonly type: 'restart' }
  | { readonly type: 'menu' };

export function createSoloState(mode: SoloMode = 'laugh'): SoloState {
  return {
    phase: 'intro',
    mode,
    calibrationError: null,
    startedAt: 0,
    pausedAt: 0,
    pausedTotal: 0,
    heldMs: null,
  };
}

const calibrating = (mode: SoloMode): SoloState => ({
  ...createSoloState(mode),
  phase: 'calibrating',
});

export function soloStep(s: SoloState, e: SoloEvent): SoloState {
  switch (e.type) {
    case 'start':
      return s.phase === 'intro' ? calibrating(e.mode) : s;
    case 'calibrated':
      return s.phase === 'calibrating' ? { ...s, phase: 'countdown', calibrationError: null } : s;
    case 'calibrationFailed':
      return s.phase === 'calibrating' ? { ...s, calibrationError: e.error } : s;
    case 'countdownDone':
      return s.phase === 'countdown'
        ? { ...s, phase: 'playing', startedAt: e.t, pausedTotal: 0 }
        : s;
    case 'lost':
      return s.phase === 'playing'
        ? { ...s, phase: 'over', heldMs: Math.max(0, e.t - s.startedAt - s.pausedTotal) }
        : s;
    case 'faceLost':
      return s.phase === 'playing' ? { ...s, phase: 'paused', pausedAt: e.t } : s;
    case 'faceBack':
      return s.phase === 'paused'
        ? { ...s, phase: 'playing', pausedTotal: s.pausedTotal + (e.t - s.pausedAt) }
        : s;
    case 'restart':
      return s.phase === 'over' ? calibrating(s.mode) : s;
    case 'menu':
      return s.phase === 'intro' ? s : createSoloState(s.mode);
  }
}

/** Temps de jeu écoulé (hors pauses) : sert au chrono et à l'intensité du miroir. */
export function elapsedPlayMs(s: SoloState, now: number): number {
  switch (s.phase) {
    case 'playing':
      return now - s.startedAt - s.pausedTotal;
    case 'paused':
      return s.pausedAt - s.startedAt - s.pausedTotal;
    case 'over':
      return s.heldMs ?? 0;
    default:
      return 0;
  }
}
